import React, { useMemo, useState, useEffect, useRef } from 'react';
import { TextField, MenuItem } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import InputAdornment from '@mui/material/InputAdornment';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import MySearchIcon from '../../MySearchIcon';
import { useToast } from 'use-toast-mui';
import { useForm, useWatch } from 'react-hook-form';
import { useDirtyTracker } from '../../../context/UpdateContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FormInputText } from '../../../form-components/FormInputText';
import { FormInputDate } from '../../../form-components/FormInputDate';
import { FormInputRadio } from '../../../form-components/FormInputRadio';
import { FormInputPhone } from '../../../form-components/FormInputPhone';
import { FormInputTextArea } from '../../../form-components/FormInputTextArea';
import { FormInputDropdown } from '../../../form-components/FormInputDropdown';
import { gender, scriptUrl } from '../../../constants/constants';
import { converterJsonToFormData, errorMessages, formatPhone } from '../../../utils/methods';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  createTemporaryUser,
  updateTemporaryUser,
  getTemporaryUserById,
  deleteTemporaryUser,
  searchUserByPhoneNumber,
} from '../../../api/temporaryUser';
import { getConsultationList } from '../../../api/consultation';
import { getBranchManager } from '../../../api/staff';
import { directMemberAppLink, getMemberAppLinkHistory } from '../../../api/memberAppLink';
import { parseExercisePurposes } from '../../../constants/exercisePurpose';
import {
  getConsultMethodLabel,
  getConsultPurposeLabel,
  getConsultResultLabel,
  CONSULT_RESULT_COLORS,
} from '../../../constants/consultation';
import useConsultOptions from '../../../hooks/useConsultOptions';
import { VISIT_PATH_ETC_MAX_LENGTH, VISIT_PATH_OTHER } from '../../../constants/visitPath';
import BannerDeleteModal from '../../banner/BannerDeleteModal';
import EnterSaleInfoModal from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModal';
import AppLinkStatus from '../../memberAppLink/AppLinkStatus';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import AppLinkModal from '../AppLinkModal';
import PhoneDuplicateModal from '../PhoneDuplicateModal';
import ConfirmModal from '../../common/modal/ConfirmModal';
import {
  ModalPageContainer,
  PageContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  HeaderRow,
  HeaderActions,
  EditActionGroup,
  BackLink,
  PageTitle,
  ListLink,
  FormContainer,
  FieldLabelRow,
  FieldLabel,
  RequiredBadge,
  PhoneLabelRow,
  DetailName,
  DetailPhone,
  DetailTitleRow,
  DetailTypeBadge,
  AddressHintText,
  AddressFieldGroup,
  ButtonRow,
  ButtonRowSpaceBetween,
  CardGrid,
  InfoCard,
  CardTitleRow,
  CardTitleBar,
  CardTitleText,
  SectionSubText,
  FieldGroup,
  FieldRow,
  FieldRowLead,
  PurposeRow,
  PurposeCount,
  PurposeChips,
  PurposeChip,
  PurposeChipMark,
  ConsultLogList,
  ConsultLogItem,
  ConsultLogHead,
  ConsultLogDate,
  ConsultLogMethod,
  ConsultLogResult,
  ConsultLogManager,
  ConsultLogContent,
  EmptyText,
  AppLinkBox,
  AppLinkBoxTitle,
  AppLinkBoxHint,
  AppLinkBoxButton,
} from '../TemporaryUserStyled';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonDeleteModal from '../../../form-components/CommonDeleteModal';

const SMS_OPTIONS = [
  { label: '동의', value: true },
  { label: '미동의', value: false },
];

const CONVERSION_RATE_OPTIONS = [
  { label: '아주높음', value: 'VERY_HIGH' },
  { label: '높음', value: 'HIGH' },
  { label: '중간', value: 'MEDIUM' },
  { label: '낮음', value: 'LOW' },
  { label: '아주낮음', value: 'VERY_LOW' },
];

const BackIcon = () => (
  <svg id="뒤로가기" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path id="Path_6534" data-name="Path 6534" d="M0,0H24V24H0Z" fill="none" />
    <circle id="Ellipse_96" data-name="Ellipse 96" cx="11" cy="11" r="11" transform="translate(1 1)" fill="#3a3a3a" />
    <path
      id="Path_6535"
      data-name="Path 6535"
      d="M6.393,8.579H14V9.865H6.393l3.352,3.448-.884.909L4,9.222l4.861-5,.884.909Z"
      transform="translate(3 2.778)"
      fill="#fff"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const STATUS_LINKED = 'LINKED';
const STATUS_INVITED = 'INVITED';

const TemporaryUserEdit = ({ onClose, isModal }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const id = isModal ? undefined : params.id;
  const user = useSelector(state => state.user.user);
  const userBranch = user?.branch;
  const token = user?.token;

  const isEditMode = !!id;
  const { visitPaths, consultMethods, exercisePurposes: purposeOptions } = useConsultOptions();

  // 관리 담당자 옵션. 응답의 managerId 는 employee 의 user ID 라서 저장에 쓰면 안 된다.
  const { data: managerOptions = [] } = useQuery({
    queryKey: ['temporaryUserManagers', userBranch?.branchId],
    queryFn: async () => {
      const rows = await getBranchManager(userBranch?.branchId, token);
      return (rows || []).map(row => ({ value: String(row.employeeId), label: row.managerName }));
    },
    enabled: !!userBranch?.branchId && !!token,
  });

  const { handleSubmit, control, setValue, getValues, reset, formState } = useForm({
    defaultValues: {
      gender: 'MALE',
      isSMSReceive: false,
      conversionRate: '',
      consultMethod: '',
      visitPath: '',
      visitPathEtc: '',
      managerId: '',
    },
  });
  useDirtyTracker(formState.isDirty);

  // 주소 값에 따라 endAdornment 힌트 노출 제어 (값 있으면 주소가 잘리지 않도록 힌트 숨김)
  const addressValue = useWatch({ control, name: 'address' });
  const visitPathValue = useWatch({ control, name: 'visitPath' });

  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userDetail, setUserDetail] = useState(null);

  // 신규 가입 예약은 저장 시 appLinkType(NEW 또는 UNLINK)과 함께 보낸다.
  // 등록 화면의 기존 계정(EXISTING)은 잠재회원 ID 가 있어야 초대할 수 있어 저장한 뒤 따로 보낸다.
  const [appLink, setAppLink] = useState({ appLinkType: null, username: null, password: null });
  const [appLinkModalOpen, setAppLinkModalOpen] = useState(false);
  const [headerSaleOpen, setHeaderSaleOpen] = useState(false);
  const [legacyUnlinkConfirmOpen, setLegacyUnlinkConfirmOpen] = useState(false);
  const [dupModalOpen, setDupModalOpen] = useState(false);
  // 등록 모달에서 [판매등록] 으로 진입한 경우 — 잠재회원을 먼저 만들고 그 대상으로 판매등록을 연다.
  const [saleTarget, setSaleTarget] = useState(null);
  const [isCreatingForSale, setIsCreatingForSale] = useState(false);
  // [등록] 인지 [판매등록] 인지 — 검증·중복확인 게이트를 공유하고 마지막 동작만 갈라진다.
  const nextActionRef = useRef('save');
  const inFlightRef = useRef(false);
  // 중복 확인 모달을 거쳐 저장할 때 쓸 값. getValues() 는 submit 이 맞춘
  // 생년월일 포맷과 전화번호 대시를 갖고 있지 않아 그대로 쓰면 값이 샌다.
  const pendingValuesRef = useRef(null);
  // 중복 확인 모달의 확정 버튼 연타 가드. 모달이 닫히기 전에 두 번째 클릭이 통과해
  // 잠재회원이 여러 건 만들어지는 것을 막는다.
  const dupConfirmInFlightRef = useRef(false);
  // 판매등록 모달에서 실제로 저장이 일어났는지. 취소로 닫은 경우와 구분한다.
  const saleSavedRef = useRef(false);
  const [dupMembers, setDupMembers] = useState([]);
  // 판매등록을 취소로 닫았을 때, 그 직전에 만든 잠재회원을 지울지 묻기 위한 대상 id.
  const [saleCanceledTempId, setSaleCanceledTempId] = useState(null);

  const openPostcodePopup = useDaumPostcodePopup(scriptUrl);

  // 편집 모드일 때 기존 데이터 조회
  const { data: userData } = useQuery({
    queryKey: ['temporaryUser', id],
    queryFn: () => getTemporaryUserById(id, token),
    enabled: isEditMode && !!token,
  });

  // 연동됨·초대 중 여부. 헤더의 AppLinkStatus 와 같은 쿼리 키라 한 번만 받는다.
  const { data: appLinkHistory = [] } = useQuery({
    queryKey: ['memberAppLinkHistory', 'TEMPORARY_USER', Number(id)],
    queryFn: () => getMemberAppLinkHistory({ targetTemporaryUserId: id }),
    enabled: isEditMode,
  });
  const activeLink = appLinkHistory.find(item => item.status === STATUS_LINKED || item.status === STATUS_INVITED);

  // linkedUserId: 앱 계정과 연동된 잠재회원이면 판매등록의 같은 번호 확인을 건너뛴다(판매 대상은 서버가 잠재회원 USER_ID 로 정한다).
  const userInfo = isEditMode ? { userId: id, userType: 'temporary', linkedUserId: userData?.linkedUserId } : null;

  // 상세 화면 상담 이력
  const { data: consultations } = useQuery({
    queryKey: ['temporaryUserConsultations', id],
    queryFn: () => getConsultationList({ temporaryUserId: id }, token),
    enabled: isEditMode && !!token,
  });

  // 상담 이력 → 그 날짜의 스케줄로 이동하고 해당 카드에 포커스
  const goToConsultationSchedule = log => {
    const consultationId = log.consultationId ?? log.id;
    if (!log.consultDate || !consultationId) return;
    navigate(`/private?date=${log.consultDate}&consultationId=${consultationId}`);
  };

  const consultationList = useMemo(() => {
    const content = consultations?.content ?? consultations;
    return Array.isArray(content) ? content : [];
  }, [consultations]);

  // 연동·취소로 상세가 다시 받아져도 직원이 고치던 폼 값을 날리지 않는다.
  const loadedIdRef = useRef(null);
  const isDirtyRef = useRef(false);
  isDirtyRef.current = formState.isDirty;

  useEffect(() => {
    if (!userData) return;
    const isFirstLoad = loadedIdRef.current !== id;
    if (isFirstLoad || !isDirtyRef.current) {
      reset({
        ...userData,
        isSMSReceive: userData.smsReceiveYn === 'Y',
        managerId: userData.managerId ? String(userData.managerId) : '',
      });
    }
    if (isFirstLoad) {
      setSelectedPurposes(parseExercisePurposes(userData.exercisePurposes));
      setAppLink({ appLinkType: null, username: null, password: null });
      loadedIdRef.current = id;
    }
    setUserDetail({
      memberId: userData.memberId || id,
      name: `${userData.name} (${userData.phoneNumber})`,
      phoneNumber: userData.phoneNumber,
    });
  }, [userData, reset, id]);

  // 저장 성공 후 네비게이션 (생성 / 수정 공통)
  const navigateAfterSave = () => {
    if (isModal && onClose) onClose();
    if (user?.userRole !== 'ROLE_SUPERADMIN') navigate('/temporary');
  };

  // 등록 화면에서 고른 기존 앱 계정으로, 방금 만든 잠재회원 초대를 보낸다. 실패해도 잠재회원은 이미 저장돼 있다.
  const inviteExistingAfterCreate = async createdId => {
    if (appLink.appLinkType !== 'EXISTING') return;
    if (!createdId) {
      toast.warning('잠재회원은 등록되었습니다. 앱 연동 초대는 잠재회원 상세에서 다시 보내 주세요.');
      return;
    }
    try {
      const result = await directMemberAppLink({
        branchId: userBranch?.branchId,
        appUserId: appLink.appUserId,
        targetType: 'TEMPORARY_USER',
        targetTemporaryUserId: createdId,
      });
      toast.success(
        result?.status === STATUS_LINKED ? '앱 계정과 연동되었습니다.' : '초대를 보냈습니다. 회원이 앱에서 동의하면 연동됩니다.',
      );
    } catch (error) {
      toast.error(`잠재회원은 등록되었지만 앱 연동 초대를 보내지 못했습니다. ${errorMessages(error)}`);
    }
  };

  const createMutation = useMutation({
    mutationFn: formData => createTemporaryUser(formData),
    onSuccess: async created => {
      toast.success(t('Successfully updated'));
      queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });
      await inviteExistingAfterCreate(created?.id);
      navigateAfterSave();
    },
  });

  const updateMutation = useMutation({
    mutationFn: formData => updateTemporaryUser(formData, id),
    onSuccess: () => {
      toast.success(t('Successfully updated'));
      queryClient.invalidateQueries({ queryKey: ['temporaryUser', id] });
      queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });
      navigate('/temporary');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTemporaryUser(id, token),
    onSuccess: () => {
      toast.success(t('Successfully delete'));
      queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });
      navigate('/temporary');
    },
  });

  const handleAddressComplete = data => {
    const fullAddress = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
    setValue('address', fullAddress);
    setValue('postalCode', data.zonecode);
  };

  const handleAddressClick = () => {
    openPostcodePopup({ onComplete: handleAddressComplete });
  };

  const togglePurpose = value => {
    setSelectedPurposes(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  // 예약 해제는 저장 시 UNLINK 로 나간다. 아직 저장 전인 예약은 그냥 비운다.
  // 저장된 예약이 있으면 새 예약을 취소해도 UNLINK 를 유지한다(마지막 의도가 해제).
  const handleCancelReservation = () => {
    setAppLink({
      appLinkType: isEditMode && userData?.appUsername ? 'UNLINK' : null,
      username: null,
      password: null,
    });
  };

  // 예전 방식으로 계정만 걸린 잠재회원 — 해제는 저장 시 UNLINK 로 나간다.
  const handleConfirmUnlinkLegacy = () => {
    setAppLink({ appLinkType: 'UNLINK', username: null, password: null });
    setLegacyUnlinkConfirmOpen(false);
  };

  const handleApplyAppLink = next => {
    setAppLink(next);
    setAppLinkModalOpen(false);
  };

  const buildFormData = values => {
    const payload = {
      ...values,
      smsReceiveYn: values.isSMSReceive ? 'Y' : 'N',
      exercisePurposes: selectedPurposes.join(','),
      ...(isEditMode ? {} : { branchId: userBranch?.branchId }),
    };

    if (appLink.appLinkType === 'NEW' || appLink.appLinkType === 'UNLINK') {
      payload.appLinkType = appLink.appLinkType;
      if (appLink.appLinkType === 'NEW') {
        payload.username = appLink.username;
        payload.password = appLink.password;
      }
    }

    return converterJsonToFormData(payload);
  };

  // 잠재회원이 이미 저장된 뒤 등록 화면을 닫는다. navigateAfterSave 와 달리
  // SUPERADMIN + 비모달에서도 폼에 갇히지 않도록 항상 목록으로 보낸다.
  const closeAfterTemporaryCreated = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate('/temporary');
    }
  };

  // 잠재회원을 만들고 그 대상으로 판매등록 모달을 연다. 중복 확인은 등록과 같은 게이트를 탄다.
  const createForSale = async values => {
    setIsCreatingForSale(true);
    try {
      const created = await createTemporaryUser(buildFormData(values));
      queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });

      // 서버가 생성만 하고 id 를 돌려주지 않으면 판매등록으로 이어갈 수 없다.
      // 이 때도 잠재회원은 이미 저장된 상태라 '실패' 로 안내하면 사실과 다르다.
      if (!created?.id) {
        toast.warning('잠재회원은 등록되었습니다. 판매등록은 잠재회원 상세에서 진행해주세요.');
        closeAfterTemporaryCreated();
        return;
      }
      await inviteExistingAfterCreate(created.id);
      setSaleTarget({
        userInfo: { userId: created.id, userType: 'temporary' },
        userDetail: {
          memberId: created.id,
          name: `${values.name} (${values.phoneNumber || ''})`,
          phoneNumber: values.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsCreatingForSale(false);
    }
  };

  const runSave = values => {
    const formData = buildFormData(values);
    if (isEditMode) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const proceed = async values => {
    if (nextActionRef.current === 'sale') {
      await createForSale(values);
    } else {
      runSave(values);
    }
  };

  // 중복 확인 왕복 동안에는 isCreatingForSale 이 아직 false 라 버튼 disabled 만으로는
  // 연타를 못 막는다. 연타하면 잠재회원이 두 번 만들어진다.
  const runAction = action => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    nextActionRef.current = action;
    handleSubmit(submit)().finally(() => {
      inFlightRef.current = false;
    });
  };

  const submit = async values => {
    if (values.birthDate) {
      const formattedDate = moment(values.birthDate).format('YYYY-MM-DD');
      values.birthDate = formattedDate;
      if (values.birthDate === 'Invalid date') {
        values.birthDate = null;
        return;
      }
    }

    if (values.postalCode && values.postalCode.length > 10) {
      toast.error('우편번호는 10자리를 초과할 수 없습니다');
      return;
    }

    if (values.phoneNumber) {
      values.phoneNumber = formatPhone(values.phoneNumber);
    }

    // 등록에서만 같은 번호 고객을 확인한다. 이 지점 회원과 이 지점 잠재회원만 보여 준다(2026-10-21, 잠재회원 10-28).
    if (!isEditMode && values.phoneNumber) {
      try {
        const results = await searchUserByPhoneNumber(
          values.phoneNumber.replaceAll('-', ''),
          userBranch?.branchId,
          true,
          true,
        );
        if (results && results.length > 0) {
          setDupMembers(results);
          setDupModalOpen(true);
          dupConfirmInFlightRef.current = false;
          pendingValuesRef.current = values;
          return;
        }
      } catch {
        // 조회가 죽으면 중복 게이트가 통째로 사라진다. 등록을 막지는 않되 그 사실은 알린다.
        toast.warning('같은 번호 고객 확인에 실패했습니다. 중복 여부를 확인하지 못한 채 등록됩니다.');
      }
    }

    await proceed(values);
  };

  const handleConfirmDuplicate = () => {
    if (dupConfirmInFlightRef.current) return;
    dupConfirmInFlightRef.current = true;
    setDupModalOpen(false);
    const values = pendingValuesRef.current ?? getValues();
    pendingValuesRef.current = null;
    proceed(values);
  };

  const handleClose = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  // 판매등록을 마치거나 중단하면 잠재회원은 이미 저장돼 있으므로 등록 화면까지 함께 닫는다.
  const handleSaleModalClose = () => {
    const saved = saleSavedRef.current;
    saleSavedRef.current = false;
    const createdId = saleTarget?.userInfo?.userId;
    setSaleTarget(null);
    queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });

    // 회원 목록은 react-query 가 아니라 redux(userList) 로 읽어서 무효화가 닿지 않는다.
    // 저장까지 마친 경우에만 현재 화면을 다시 읽어 새 회원이 빠지지 않게 한다.
    if (saved) {
      window.location.reload();
      return;
    }
    // 판매를 취소했다면 그 직전에 만들어진 잠재회원만 남는다. 지울지 여기서 묻는다.
    if (createdId) {
      setSaleCanceledTempId(createdId);
      return;
    }
    closeAfterTemporaryCreated();
  };

  // 판매등록 취소 후 '삭제' 를 고른 경우. 실패해도 화면은 닫고 잠재회원은 남는다.
  const handleDeleteCanceledTemporary = async () => {
    const targetId = saleCanceledTempId;
    setSaleCanceledTempId(null);
    try {
      await deleteTemporaryUser(targetId, token);
      queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });
      toast.success('잠재회원이 삭제되었습니다');
    } catch (error) {
      toast.error(errorMessages(error));
    }
    closeAfterTemporaryCreated();
  };

  const handleKeepCanceledTemporary = () => {
    setSaleCanceledTempId(null);
    closeAfterTemporaryCreated();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  const renderHeader = () => {
    if (isModal) {
      return (
        <ModalHeader>
          <ModalTitle>{t('잠재회원등록')}</ModalTitle>
          <ModalCloseButton onClick={onClose}>
            <CloseIcon />
          </ModalCloseButton>
        </ModalHeader>
      );
    }

    return (
      <>
        <HeaderRow>
          <BackLink as={Link} onClick={() => navigate(-1)}>
            <BackIcon />
            {isEditMode ? (
              <DetailTitleRow>
                <DetailName>{userData?.name || '-'}</DetailName>
                <DetailTypeBadge>잠재회원</DetailTypeBadge>
                <DetailPhone>{userData?.phoneNumber || '-'}</DetailPhone>
              </DetailTitleRow>
            ) : (
              <PageTitle>{t('Add Client')}</PageTitle>
            )}
          </BackLink>

          <HeaderActions>
            {/* BackLink 안에 두면 클릭이 navigate(-1) 로 번지므로 형제로 배치한다 */}
            {isEditMode && (
              <AppLinkStatus
                target={{
                  type: 'TEMPORARY_USER',
                  id: Number(id),
                  // 서버는 잠재회원 소속 지점으로 연동하므로 칩도 그 지점 기준(없으면 로그인 지점)
                  branchId: userData?.branchId ?? userBranch?.branchId,
                  name: userData?.name,
                  phoneNumber: userData?.phoneNumber,
                }}
              />
            )}
            {isEditMode && (
              <EnterSaleInfoModal
                userInfo={userInfo}
                userDetail={userDetail}
                open={headerSaleOpen}
                // 판매에 성공하면 /temporary 로 이동해 폼에 담긴 앱 연동(신규 가입·해제)이 버려지므로 먼저 저장하게 한다.
                onOpen={() => {
                  if (appLink.appLinkType === 'NEW' || appLink.appLinkType === 'UNLINK') {
                    toast.warning('저장하지 않은 앱 연동이 있습니다. 먼저 저장해 주세요.');
                    return;
                  }
                  setHeaderSaleOpen(true);
                }}
                onClose={() => setHeaderSaleOpen(false)}
                openButtonSx={{ background: '#4AB3BC', '&:hover': { backgroundColor: '#3196A0' } }}
              />
            )}
            <ListLink as={Link} to="/temporary">
              {t('List')}
            </ListLink>
          </HeaderActions>
        </HeaderRow>
        <hr />
      </>
    );
  };

  // 연동됨·초대 중이면 상태와 취소는 헤더의 AppLinkStatus 가 맡고, 여기서는 시작 버튼을 숨긴다.
  const renderAppLinkBox = () => {
    const reservedUsername =
      appLink.appLinkType === 'NEW'
        ? appLink.username
        : appLink.appLinkType === 'UNLINK'
          ? null
          : userData?.appUsername;
    const isReservationUnsaved = appLink.appLinkType === 'NEW';

    // 연동 기록(member_app_link) 없이 USER_ID 만 걸린 예전 연결
    const isLegacyLinked = !!userData?.linkedUserId && !activeLink;
    const isUnlinkPending = appLink.appLinkType === 'UNLINK';

    let content;
    if (isLegacyLinked) {
      content = isUnlinkPending ? (
        <>
          <AppLinkBoxHint>연결 해제 예정 (저장하면 적용)</AppLinkBoxHint>
          <AppLinkBoxButton
            type="button"
            variant="unlink"
            onClick={() => setAppLink({ appLinkType: null, username: null, password: null })}>
            취소
          </AppLinkBoxButton>
        </>
      ) : (
        <>
          <AppLinkBoxHint>앱 계정 연결됨 ({userData.linkedUsername || '-'})</AppLinkBoxHint>
          <AppLinkBoxButton type="button" variant="unlink" onClick={() => setLegacyUnlinkConfirmOpen(true)}>
            연결 해제
          </AppLinkBoxButton>
        </>
      );
    } else if (activeLink) {
      content = <AppLinkBoxHint>{activeLink.status === STATUS_LINKED ? '앱 연동됨' : '초대 중'}</AppLinkBoxHint>;
    } else if (appLink.appLinkType === 'EXISTING') {
      content = (
        <>
          <AppLinkBoxHint>기존 계정 {appLink.username} (저장하면 초대)</AppLinkBoxHint>
          <AppLinkBoxButton type="button" variant="unlink" onClick={handleCancelReservation}>
            취소
          </AppLinkBoxButton>
        </>
      );
    } else if (reservedUsername) {
      content = (
        <>
          {/* 신규 가입은 저장하면 바로 로그인되는 계정이 생긴다(2026-10-22). 저장된 예약 아이디는 예전 방식(판매 때 생성) 데이터 */}
          <AppLinkBoxHint>
            {isReservationUnsaved
              ? `아이디 ${reservedUsername} (저장하면 계정 생성)`
              : `예약 아이디 ${reservedUsername}`}
          </AppLinkBoxHint>
          <AppLinkBoxButton type="button" variant="unlink" onClick={handleCancelReservation}>
            {isReservationUnsaved ? '취소' : '예약 해제'}
          </AppLinkBoxButton>
        </>
      );
    } else {
      content = (
        <>
          {isUnlinkPending && <AppLinkBoxHint>예약 해제 예정 (저장하면 적용)</AppLinkBoxHint>}
          <AppLinkBoxButton type="button" onClick={() => setAppLinkModalOpen(true)}>
            연동하기
          </AppLinkBoxButton>
        </>
      );
    }

    return (
      <AppLinkBox>
        <AppLinkBoxTitle>
          <PhoneIphoneRoundedIcon />
          APP 연동
        </AppLinkBoxTitle>
        {content}
      </AppLinkBox>
    );
  };

  const renderBasicCard = () => (
    <InfoCard>
      <CardTitleRow>
        <CardTitleBar />
        <CardTitleText>기본 정보</CardTitleText>
      </CardTitleRow>

      <FieldGroup>
        <FieldLabelRow>
          <FieldLabel>이름</FieldLabel>
          <RequiredBadge>필수</RequiredBadge>
        </FieldLabelRow>
        <FormInputText
          color="info"
          control={control}
          rules={{ required: true }}
          size="small"
          fullWidth
          variant="outlined"
          name="name"
          placeholder="이름을 입력해 주세요"
        />
      </FieldGroup>

      <FieldGroup>
        <PhoneLabelRow>
          <FieldLabel>휴대폰 번호</FieldLabel>
          <RequiredBadge>필수</RequiredBadge>
        </PhoneLabelRow>
        <FormInputPhone
          control={control}
          rules={{ required: true }}
          setValue={setValue}
          name="phoneNumber"
          placeholder="휴대폰 번호를 입력해 주세요"
        />
      </FieldGroup>

      <FieldRowLead>
        <FieldGroup>
          <FieldLabel>성별</FieldLabel>
          <FormInputRadio name="gender" control={control} options={gender} row />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>생년월일</FieldLabel>
          <FormInputDate
            name="birthDate"
            control={control}
            placeholder="YYYY-MM-DD"
            renderInput={inputParams => <TextField size="small" {...inputParams} />}
          />
        </FieldGroup>
      </FieldRowLead>

      <FieldGroup>
        <FieldLabel>이메일</FieldLabel>
        <FormInputText
          color="info"
          control={control}
          type="email"
          size="small"
          fullWidth
          variant="outlined"
          name="email"
          placeholder="이메일을 입력해 주세요"
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>주소</FieldLabel>
        <AddressFieldGroup>
          <FormInputText
            color="info"
            control={control}
            name="address"
            onClick={handleAddressClick}
            onMouseOver={e => (e.target.style.cursor = 'pointer')}
            placeholder="주소/우편번호 검색"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {!addressValue && <AddressHintText>도로명, 지번, 건물명 검색</AddressHintText>}
                  <MySearchIcon onClick={handleAddressClick} />
                </InputAdornment>
              ),
            }}
            size="small"
            fullWidth
            variant="outlined"
          />
          <FormInputText
            color="info"
            control={control}
            size="small"
            fullWidth
            variant="outlined"
            name="addressDetail"
            placeholder="상세 주소 입력"
          />
        </AddressFieldGroup>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>광고성 정보 수신 동의 (SMS·알림톡)</FieldLabel>
        <FormInputRadio name="isSMSReceive" control={control} options={SMS_OPTIONS} row />
      </FieldGroup>
    </InfoCard>
  );

  const renderConsultCard = () => (
    <InfoCard>
      {renderAppLinkBox()}

      <FieldGroup>
        <PurposeRow>
          <FieldLabel>운동목적</FieldLabel>
          <SectionSubText>복수 선택 가능</SectionSubText>
          <PurposeCount>{selectedPurposes.length}개 선택</PurposeCount>
        </PurposeRow>
        <PurposeChips>
          {purposeOptions.map(option => {
            const isActive = selectedPurposes.includes(option.value);
            return (
              <PurposeChip key={option.value} isActive={isActive} onClick={() => togglePurpose(option.value)}>
                <PurposeChipMark>{isActive ? '✓' : '+'}</PurposeChipMark>
                {option.label}
              </PurposeChip>
            );
          })}
        </PurposeChips>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>회원 전환 확률</FieldLabel>
        <FormInputDropdown
          control={control}
          name="conversionRate"
          options={CONVERSION_RATE_OPTIONS}
          disabledItem={
            <MenuItem value="" disabled>
              선택안함
            </MenuItem>
          }
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>관리 담당자</FieldLabel>
        <FormInputDropdown
          control={control}
          name="managerId"
          options={managerOptions}
          disabledItem={
            <MenuItem value="" disabled>
              담당자 없음
            </MenuItem>
          }
        />
      </FieldGroup>

      {/* 선택안함은 고를 수 있다 — 수정에서 고르면 서버가 값을 지운다 */}
      <FieldGroup>
        <FieldRow>
          <FieldGroup>
            <FieldLabel>방문경로</FieldLabel>
            <FormInputDropdown
              control={control}
              name="visitPath"
              options={visitPaths}
              disabledItem={<MenuItem value="">선택안함</MenuItem>}
            />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>상담수단</FieldLabel>
            <FormInputDropdown
              control={control}
              name="consultMethod"
              options={consultMethods}
              disabledItem={<MenuItem value="">선택안함</MenuItem>}
            />
          </FieldGroup>
        </FieldRow>
        {visitPathValue === VISIT_PATH_OTHER && (
          <FormInputText
            color="info"
            control={control}
            size="small"
            fullWidth
            variant="outlined"
            name="visitPathEtc"
            placeholder={`방문경로 직접 입력 (${VISIT_PATH_ETC_MAX_LENGTH}자 이내)`}
            inputProps={{ maxLength: VISIT_PATH_ETC_MAX_LENGTH }}
          />
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>메모</FieldLabel>
        <FormInputTextArea
          control={control}
          minRows={6}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '4px',
            borderColor: 'rgba(133, 133, 133, 0.5)',
            resize: 'none',
          }}
          name="memo"
          placeholder="상담 내용을 입력해 주세요. (예: 26.07.29 TM / 내일 오후 7시 방문 예정)"
        />
      </FieldGroup>
    </InfoCard>
  );

  const renderConsultationHistoryCard = () => (
    <InfoCard>
      <CardTitleRow>
        <CardTitleBar />
        <CardTitleText>상담 이력</CardTitleText>
        <SectionSubText>{consultationList.length}건</SectionSubText>
      </CardTitleRow>

      {consultationList.length === 0 ? (
        <EmptyText>등록된 상담 이력이 없습니다.</EmptyText>
      ) : (
        <ConsultLogList>
          {consultationList.map(log => (
            <ConsultLogItem
              key={log.consultationId ?? log.id}
              onClick={() => goToConsultationSchedule(log)}
              title="스케줄에서 보기">
              <ConsultLogHead>
                <ConsultLogDate>{log.consultDate || '-'}</ConsultLogDate>
                <ConsultLogResult resultColor={CONSULT_RESULT_COLORS[log.consultResult || 'SCHEDULED']}>
                  {getConsultResultLabel(log.consultResult || 'SCHEDULED')}
                </ConsultLogResult>
                {log.consultMethod && (
                  <ConsultLogMethod>
                    {log.consultMethodDescription || getConsultMethodLabel(log.consultMethod)}
                  </ConsultLogMethod>
                )}
                {log.consultPurpose && (
                  <ConsultLogMethod>
                    {log.consultPurposeDescription || getConsultPurposeLabel(log.consultPurpose)}
                  </ConsultLogMethod>
                )}
                <ConsultLogManager>{log.managerName || '-'}</ConsultLogManager>
              </ConsultLogHead>
              {/* 스케줄에서 만든 상담은 내용이 비어 있을 수 있어 제목으로 대신 보여준다 */}
              <ConsultLogContent>{log.content || log.title || '-'}</ConsultLogContent>
            </ConsultLogItem>
          ))}
        </ConsultLogList>
      )}
    </InfoCard>
  );

  const renderButtons = () => {
    if (isEditMode) {
      return (
        <ButtonRowSpaceBetween>
          <CommonModalButton variant="delete" onClick={() => setDeleteModalOpen(true)}>
            삭제
          </CommonModalButton>
          <EditActionGroup>
            <CommonModalButton variant="cancel" onClick={() => navigate(-1)}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSubmit(submit)} disabled={isPending}>
              저장
            </CommonModalButton>
          </EditActionGroup>
        </ButtonRowSpaceBetween>
      );
    }

    return (
      <ButtonRow>
        <CommonModalButton variant="cancel" onClick={handleClose}>
          취소
        </CommonModalButton>
        <CommonModalButton
          variant="submit"
          onClick={() => runAction('save')}
          disabled={isPending || isCreatingForSale}>
          등록
        </CommonModalButton>
        <CommonModalButton
          variant="dark"
          onClick={() => runAction('sale')}
          disabled={isPending || isCreatingForSale}>
          {isCreatingForSale ? '처리 중...' : '판매등록'}
        </CommonModalButton>
      </ButtonRow>
    );
  };

  const Container = isModal ? ModalPageContainer : PageContainer;

  return (
    <Container as="div" isModal={isModal}>
      {renderHeader()}

      <ModalBody isModal={isModal}>
        <FormContainer isModal={isModal} className="form1">
          <CardGrid columns={isEditMode ? 3 : 2}>
            {renderBasicCard()}
            {renderConsultCard()}
            {isEditMode && renderConsultationHistoryCard()}
          </CardGrid>
        </FormContainer>

        {renderButtons()}
      </ModalBody>

      <CommonDeleteModal
        open={legacyUnlinkConfirmOpen}
        onClose={() => setLegacyUnlinkConfirmOpen(false)}
        onConfirm={handleConfirmUnlinkLegacy}
        title="앱 계정 연결을 해제하시겠습니까?"
        description="연결을 해제하면 판매등록이 이 앱 계정으로 가지 않습니다. 앱 계정 자체는 삭제되지 않습니다."
        confirmText="연결 해제"
      />
      <AppLinkModal
        open={appLinkModalOpen}
        onClose={() => setAppLinkModalOpen(false)}
        onApply={handleApplyAppLink}
        target={isEditMode ? { type: 'TEMPORARY_USER', id: Number(id), name: userData?.name } : { type: 'TEMPORARY_USER' }}
      />

      {/* [판매등록] 진입 — 잠재회원을 만든 뒤 그 대상으로 연다 */}
      {saleTarget && (
        <EnterSaleInfoModal
          userInfo={saleTarget.userInfo}
          userDetail={saleTarget.userDetail}
          open
          hideButton
          onClose={handleSaleModalClose}
          onSaved={() => {
            saleSavedRef.current = true;
          }}
          redirectOnTemporarySuccess={false}
          skipTemporaryDupCheck
          closeConfirmMessage={'판매등록을 그만두시겠습니까?'}
        />
      )}

      {/* 판매등록 취소 2단계 — 방금 만든 잠재회원을 지울지 묻는다 */}
      <ConfirmModal
        open={!!saleCanceledTempId}
        onClose={handleKeepCanceledTemporary}
        onConfirm={handleDeleteCanceledTemporary}
        message={'방금 등록한 잠재회원도 삭제할까요?\n삭제하면 되돌릴 수 없습니다.'}
        cancelText="유지"
        confirmText="삭제"
        confirmColor="#F44336"
      />

      <PhoneDuplicateModal
        open={dupModalOpen}
        onClose={() => setDupModalOpen(false)}
        onConfirm={handleConfirmDuplicate}
        members={dupMembers}
      />

      {/* 삭제 확인 모달 (편집 모드에서만 사용) */}
      {isEditMode && (
        <BannerDeleteModal open={deleteModalOpen} setOpen={setDeleteModalOpen} handleDelete={handleDelete} />
      )}
    </Container>
  );
};

export default TemporaryUserEdit;
